import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FeatureToggleService } from '@nx-fe/services';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FileManagerState, FileSearchState } from '../../file-manager/FileManagerState';
import { FileManagerService } from '../../services/file-manager.service';
import { ITabViewState } from '../../services/rc-tab.service';
import { RemoteControlService } from 'xsmodules/remote-control/services/remote-control.service';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';
import { SpecialKeyCode } from 'app/modules/remote-control-ng/definitions';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';

@Component({
  selector: 'app-file-explorer',
  templateUrl: './file-explorer.component.html',
  styleUrls: ['./file-explorer.component.scss'],
  host: {class: 'soti-scroll-bar-y'}
})
export class FileExplorerComponent implements OnInit, OnDestroy {
  private _tabViewState: ITabViewState;
  public set tabViewState(tabViewState: ITabViewState) {
    this._tabViewState = tabViewState;

    // Fire onTabShow if this tab is initially selected
    if (this.tabViewState && this.tabViewState.enabled && this.tabViewState.selected) {
      this.showComponent();
    }
  }

  public get tabViewState(): ITabViewState {
    return this._tabViewState;
  }

  private _state: FileManagerState;
  subscribedToRcStarted = false;
  destroyed$ = new Subject<boolean>();

  private readonly remoteControlEndedMessageForPopOut: BroadcastService<string> = new BroadcastService(
    BroadcastChannels.onRemoteControlEndedMessageForPopOut
  );

  constructor(
    private remoteControlService: RemoteControlService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private fileManagerService: FileManagerService,
    private featureToggleService: FeatureToggleService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.checkPermissions();
    this.remoteControlService.remoteControlEnded$.pipe(takeUntil(this.destroyed$)).subscribe(deviceId => {
      this.resetState(deviceId);
    });

    this.remoteControlEndedMessageForPopOut.onMessage = (deviceId: string) => {
      this.resetState(deviceId);

      // Do not move this code to the $onDestroy section.
      if (this.remoteControlEndedMessageForPopOut) {
        this.remoteControlEndedMessageForPopOut.close();
      }
    };

    this.showComponent();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public isActionBarAvailableByToggle() {
    return (
      this.state &&
      this.state.isSelectedAnyNode() &&
      (this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') ||
        this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove'))
    );
  }

  public get state(): FileManagerState {
    if (!this._state) {
      this._state = this.fileManagerService.getState(RcContext.instance.sotiAssistDeviceId);
      if (this._state) {
        this._state.onUpdate = () => this.cdr.detectChanges();
      }
    }

    return this._state;
  }

  public get searchInProgress(): boolean {
    return (
      this.state &&
      (this.state.searchState === FileSearchState.Searching || this.state.searchState === FileSearchState.Cancelling)
    );
  }

  public resetState(mcDeviceId: string) {
    this.fileManagerService.resetState(mcDeviceId);

    if (this._state) {
      this._state.clearNodes();
      this._state = null;
    }
  }

  private validateCharacter = (keyCode: number): boolean => {
    const regex = /[\w\-_\. ]/;
    const key = String.fromCharCode(keyCode);

    return !regex.test(key);
  };

  public searchKeypress(event: KeyboardEvent) {
    const searchSpecialKeyCodes = [
      SpecialKeyCode.BACKSPACE,
      SpecialKeyCode.TAB,
      SpecialKeyCode.CR,
      SpecialKeyCode.END,
      SpecialKeyCode.HOME,
      SpecialKeyCode.ARROWLEFT,
      SpecialKeyCode.ARROWUP,
      SpecialKeyCode.ARROWRIGHT,
      SpecialKeyCode.ARROWDOWN,
      SpecialKeyCode.DELETE,
    ];

    if (searchSpecialKeyCodes.indexOf(event.keyCode) < 0) {
      const keyCode = event.keyCode || event.which;
      if (this.validateCharacter(keyCode)) {
        event.preventDefault();
      }
    }

    if (13 === (event.keyCode || event.which)) {
      this.state.onSearchButton();
    }
  }

  public onScroll() {
    this.state.onScroll();
  }

  public onChange = () => {
    const regex = /[^\w\-_\. ]/g;
    this.state.searchText = this.state.searchText.replace(regex, '');
  };

  checkPermissions = () => {
    if (!this.tabViewState) {
      return;
    }

    if (RcContext.instance.security.RemoteControlViewFiles !== this.tabViewState.enabled) {
      this.tabViewState.enabled = RcContext.instance.security.RemoteControlViewFiles;
      this.cdr.detectChanges();
    }
  };

  private showComponent() {
    const deviceRcState = this.deviceStateRegistry.getCurrentState();
    if ((!deviceRcState || !deviceRcState.connected) && !this.subscribedToRcStarted) {
      // If we cant query the device contents right away, then wait for the RC session to start
      // This can happen if we are restarting an RC session from FileExplorer tab
      this.subscribedToRcStarted = true;
      this.remoteControlService.remoteControlStarted$.pipe(take(1)).subscribe(() => {
        this.state.onRemoteControlStarted();
        this.checkPermissions();
        const rcState = this.deviceStateRegistry.getCurrentState();
        if (rcState && !rcState.poppedOut) {
          this.showComponent();
        }

        this.subscribedToRcStarted = false;
      });
    }

    if (!deviceRcState || deviceRcState.hasError) {
      return;
    }

    // If transfer is in progress don't getDeviceContents because data will come  from transfer
    if (deviceRcState.connected && !this.remoteControlService.tranferInProgress) {
      this.state.getDeviceContents(deviceRcState.device);
      this.state.attachToPage();
    }
  }
}
